import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import React from "react";
import { useForm } from "react-hook-form";
import type { Deal } from "~/models/Deal";
import type { DealVendorCompanyVendorUpdateParam } from "~/models/DealVendorCompany";
import {
	getOriginDealVendor,
	validationSchema
} from "~/models/DealVendorCompany";
import { isSuccess } from "~/utils/api/api_handler";
import { update } from "~/utils/api/vendors/deals/DealVendorCompany";

type Props = {
	deal: Deal;
	handleClose: () => void;
	handleMutate: () => Promise<void>;
	isOpen: boolean;
};

export const DealNoteWithinVendorModal: React.FC<Props> = ({
	deal,
	isOpen,
	handleClose,
	handleMutate
}: Props) => {
	const { notify } = useNotification();
	const originDealVendor = getOriginDealVendor(deal);
	const noteWithinVendor = originDealVendor?.noteWithinVendor ?? "";
	const {
		register,
		handleSubmit,
		formState,
		formState: { errors }
	} = useForm<DealVendorCompanyVendorUpdateParam>({
		defaultValues: { noteWithinVendor },
		resolver: yupResolver(validationSchema)
	});
	const handleModalSubmit = React.useCallback(() => {
		handleSubmit(async (submitValue: DealVendorCompanyVendorUpdateParam) => {
			if (originDealVendor === null) return;
			const response = await update(
				deal.id,
				originDealVendor,
				null,
				submitValue.noteWithinVendor
			);
			if (isSuccess(response)) {
				handleMutate();
				handleClose();
				notify({ content: "更新しました。", variant: "success" });
				return;
			}
			handleClose();
			notify({ content: "エラーが発生しました。", variant: "error" });
		})();
	}, [
		handleSubmit,
		originDealVendor,
		deal.id,
		handleClose,
		notify,
		handleMutate
	]);

	return (
		<Modalv2 isOpen={isOpen} onClose={handleClose} size="l">
			<ModalHeader
				description="施工会社のみ編集可能/実施会社は閲覧可能です。"
				title="案件メモの更新"
			/>
			<ModalBody>
				<FormControl
					isInvalid={!!errors.noteWithinVendor}
					label="社内案件メモ"
					variant="vertical"
				>
					<InputText
						isInvalid={!!errors.noteWithinVendor}
						maxRows={20}
						placeholder="社内で共有したいメモを記載してください"
						resize="autoRows"
						rows={8}
						{...register("noteWithinVendor")}
					/>
					<FormControlText help="改行、URLはマークダウンで記載可能です。改行は半角スペース2つ、URLは[リンク名](URL)で記載可能です" />
					{errors.noteWithinVendor ? (
						<FormControlText error={errors.noteWithinVendor.message || ""} />
					) : null}
				</FormControl>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} variant="secondary">
						保存せず閉じる
					</Button>
				}
				rightArea={
					<Button
						disabled={formState.isSubmitting}
						onClick={handleModalSubmit}
						variant="primary"
					>
						更新
					</Button>
				}
			/>
		</Modalv2>
	);
};
